import queryString from 'query-string';
import { getPuzzleBank } from "../constants/crosswords";
import padStart from 'lodash/padStart';
import { CrosswordInput, Direction, GridData, WordInput } from '../types';
import { Guesses } from '../redux/slices/wordleSlice';
import get from 'lodash/get';
import addDays from 'date-fns/addDays';
import differenceInDays from 'date-fns/differenceInDays';
import { GameMode, startDateForMode } from './gameMode';

export function notEmpty<TValue>(
  value: TValue | null | undefined,
): value is TValue {
  return value !== null && value !== undefined;
}

export function getPuzzleIndexForDate(date: Date, mode: GameMode = 'classic') {
  return differenceInDays(date, startDateForMode(mode));
}

export function dateFromPuzzleIndex(index: number, mode: GameMode = 'classic') {
  return addDays(startDateForMode(mode), index);
}

export function getTodaysPuzzleIndex(mode: GameMode = 'classic') {
  const bank = getPuzzleBank(mode);
  if (process.env.NODE_ENV === 'development') return Math.max(bank.length - 1, 0);

  const index = getPuzzleIndexForDate(new Date(), mode);
  return Math.min(index, bank.length - 1);
}

export function getPuzzleOfTheDay(mode: GameMode = 'classic') {
  const bank = getPuzzleBank(mode);
  let index = getTodaysPuzzleIndex(mode);
  const queryIndex = Number(queryString.parse(window.location.search)?.index as string);
  if (queryIndex >= 0) index = Math.min(index, queryIndex);

  return {
    crossword: bank[index],
    crosswordIndex: index,
  }
};

export const crosswordIndex = getTodaysPuzzleIndex('classic');
export const todaysPuzzleIndex = getTodaysPuzzleIndex('classic');

export function getInitialClue(crossword: CrosswordInput) {
  let initialClue = get(crossword, 'across.1') as WordInput;
  const initialDirection = initialClue ? 'across' : 'down' as Direction;
  if (!initialClue) initialClue = get(crossword, 'down.1');
  return { initialClue, initialDirection };
}

export function sleep(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

const padTime = (time: number) => padStart(time.toString(), 2, '0');

export function timeTillTomorrow() {
  const now = new Date();
  const hours = 23 - now.getHours();
  const minutes = 59 - now.getMinutes();
  const seconds = 59 - now.getSeconds();
  return `${padTime(hours)}:${padTime(minutes)}:${padTime(seconds)}`;
};

export function getTotalGuesses(guesses?: Guesses) {
  const countDir = (dir?: Record<string, string[]>) =>
    Object.values(dir || {}).reduce((n, list) => n + (Array.isArray(list) ? list.length : 0), 0);
  return countDir(guesses?.across) + countDir(guesses?.down);
}

export function gameProgress(gridData: GridData) {
  let totalCells = 0;
  let completedCells = 0;

  gridData.forEach((row) => {
    row.forEach((cell) => {
      if (!cell.used) return;
      totalCells += 1;
      if (cell.guess === cell.answer) completedCells += 1;
    });
  });

  return completedCells / totalCells;
};

export function dateString(date: Date) {
  return date.toISOString().split('T')[0]
}

export function formatTime(time?: number) {
  if (!time) return '0:00'
  const minutes = Math.floor(time / 60);
  const seconds = time % 60;
  return `${minutes}:${padStart(seconds.toString(), 2, '0')}`;
}
